import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ProductCarousel from './ProductCarousel';
import { CloseIcon } from './icons';
import './SummaryModal.css';

function SummaryModal({ isOpen, onClose, title, summary, trendSection, productTable, moduleId, videoUrl, trendId, summarySections }) {
  const [isClosing, setIsClosing] = useState(false);
  const summaryAnchorRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setIsClosing(false);
    }
    // Note: Trend views are now tracked via impressions (trend_impressions table)
    // when the trend card becomes visible, not when the modal opens.
    // Modal opening is tracked as 'expand' interaction in Module.js
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 300); // Match the animation duration
  };

  // Fix malformed markdown to proper markdown format (for ReactMarkdown rendering)
  const fixMarkdown = (text) => {
    if (!text) return '';
    const digitEmojiMap = {
      '0️⃣': '0.',
      '1️⃣': '1.',
      '2️⃣': '2.',
      '3️⃣': '3.',
      '4️⃣': '4.',
      '5️⃣': '5.',
      '6️⃣': '6.',
      '7️⃣': '7.',
      '8️⃣': '8.',
      '9️⃣': '9.'
    };
    Object.entries(digitEmojiMap).forEach(([emoji, replacement]) => {
      const regex = new RegExp(emoji, 'g');
      text = text.replace(regex, `${replacement} `);
    });
    // Ensure spacing around bold markers
    text = text.replace(/([^\s])\*\*/g, '$1 **'); // Add space before **
    text = text.replace(/\*\*([^\s])/g, '** $1'); // Add space after **
    text = text.replace(/([^\s])\*/g, '$1 *'); // Add space before single *
    text = text.replace(/\*([^\s])/g, '* $1'); // Add space after single *
    // Remove bold/italic markers completely
    text = text.replace(/\*\*/g, '');
    text = text.replace(/\*/g, '');
    // Clean up stray asterisks
    text = text.replace(/\s+\*\s+/g, ' ');
    return text.replace(/\s+/g, ' ').trim();
  };

  // Clean markdown for plain text display (remove all formatting)
  const cleanText = useCallback((text) => {
    if (!text) return '';
    // First fix malformed patterns, then remove all markdown
    text = fixMarkdown(text);
    // Remove all markdown formatting for plain text display
    text = text.replace(/\*\*/g, ''); // Remove bold
    text = text.replace(/\*/g, ''); // Remove italic
    return text.trim();
  }, []);

  const structuredSummarySections = useMemo(() => {
    if (summarySections && summarySections.length > 0) {
      return summarySections;
    }

    if (trendSection) {
      return [{ heading: 'The Trend', body: trendSection }];
    }

    if (summary) {
      return [{ heading: 'Summary', body: summary }];
    }

    return [];
  }, [summarySections, trendSection, summary]);

  const summaryCards = useMemo(() => {
    if (!structuredSummarySections || structuredSummarySections.length === 0) {
      return [];
    }

    return structuredSummarySections.slice(0, 2).map(section => ({
      heading: section.heading,
      body: section.body
    }));
  }, [structuredSummarySections]);

  const scrollToFullSummary = useCallback(() => {
    if (summaryAnchorRef.current) {
      summaryAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Use pre-extracted product table from backend
  // Backend extraction handles both single-line and multi-line table formats
  const productTableMarkdown = useMemo(() => {
    // Use pre-extracted table from backend (preferred)
    if (productTable) {
      return productTable;
    }
    
    // Fallback: Basic extraction from summary for legacy topics without product_table
    // Note: Backend extraction is preferred and handles both single-line and multi-line formats
    // This fallback only handles multi-line format as a safety net
    if (!summary) return null;

    // Find table section
    const tableHeaderPattern = /###\s*2\.?\s*WHERE\s+TO\s+FIND\s+IT\s*\|\s*Price\s+Point\s*\|\s*Brand\s*\|\s*Product\s*\|\s*Why\s+It\s+Works\s*\|/i;
    const headerMatch = summary.match(tableHeaderPattern) || summary.match(/###\s*WHERE\s+TO\s+FIND\s+IT\s*\|\s*Price\s+Point\s*\|\s*Brand\s*\|\s*Product\s*\|\s*Why\s+It\s+Works\s*\|/i);
    if (!headerMatch) return null;

    // Extract table header row
    const tableHeaderMatch = headerMatch[0].match(/\|\s*Price\s+Point\s*\|\s*Brand\s*\|\s*Product\s*\|\s*Why\s+It\s+Works\s*\|/i);
    if (!tableHeaderMatch) return null;

    const remainingText = summary.substring(headerMatch.index + headerMatch[0].length);
    const lines = remainingText.split('\n');
    const tableLines = [tableHeaderMatch[0].trim()];
    let foundSeparator = false;

    // Collect table rows (multi-line format only)
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        if (foundSeparator && tableLines.length > 2) break;
        continue;
      }
      if (/^\|[\s\-:]+\|$/.test(trimmed)) {
        tableLines.push(trimmed);
        foundSeparator = true;
      } else if (foundSeparator && /^\|.+\|$/.test(trimmed)) {
        tableLines.push(trimmed);
      } else if (foundSeparator && !trimmed.includes('|')) {
        break;
      }
    }

    return tableLines.length >= 2 ? tableLines.join('\n') : null;
  }, [summary, productTable]);

  // Parse product table into individual products
  const products = useMemo(() => {
    const table = productTableMarkdown;
    if (!table) return [];

    const lines = table.split('\n').filter(line => line.trim());
    if (lines.length < 2) return []; // Need at least header and separator

    // Skip header and separator rows
    const dataRows = lines.slice(2);
    const productsList = [];

    for (const row of dataRows) {
      // Parse markdown table row: | Price | Brand | Product | Why |
      const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
      
      if (cells.length >= 4) {
        // Clean text for plain display in cards (remove formatting)
        const cleanCell = (text) => {
          // Fix malformed patterns first, then remove all markdown
          return cleanText(text);
        };
        
        productsList.push({
          pricePoint: cleanCell(cells[0]),
          brand: cleanCell(cells[1]),
          product: cleanCell(cells[2]),
          whyItWorks: cleanCell(cells[3])
        });
      }
    }

    return productsList;
  }, [productTableMarkdown, cleanText]);

  // Modal content JSX
  const modalContent = (!isOpen && !isClosing) ? null : (
    <div 
      className={`modal-overlay ${isClosing ? 'closing' : ''}`} 
      onClick={handleClose}
    >
      <div className={`modal-content module-theme-${moduleId || 'default'}`} data-module-theme={moduleId || 'default'} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close-button" onClick={handleClose} aria-label="Close">
            <CloseIcon size={24} color="#1A1A1A" />
          </button>
        </div>
        <div className="modal-body">
          <div className="summary-text markdown-content">
            {/* Product Recommendations */}
            {/* Show carousel if there are products OR if there's a video (even without products) */}
            {(products.length > 0 || videoUrl) ? (
              <ProductCarousel 
                products={products} 
                videoUrl={videoUrl} 
                trendId={trendId}
                summarySections={summaryCards}
                onSummarySelect={scrollToFullSummary}
              />
            ) : productTableMarkdown ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {fixMarkdown(productTableMarkdown)}
              </ReactMarkdown>
            ) : (
              <p>No product recommendations available.</p>
            )}
            
            {/* Structured trend summary - rendered as individual sections */}
            {structuredSummarySections.length > 0 && (
              <div className="trend-section" ref={summaryAnchorRef}>
                {structuredSummarySections.map((section, index) => (
                  <div className="trend-section-block" key={`${section.heading}-${index}`}>
                    <h3>{section.heading}</h3>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {fixMarkdown(section.body)}
                    </ReactMarkdown>
                  </div>
                ))}
              </div>
            )}
            
            {/* Debug sections - only show in development */}
            {process.env.NODE_ENV === 'development' && (
              <>
                <div style={{ marginTop: '2em', paddingTop: '2em', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ fontSize: '0.9em', opacity: 0.7, marginBottom: '1em' }}>DEBUG: Extracted Table Markdown</h3>
                  <pre style={{ 
                    background: 'rgba(255, 255, 255, 0.02)', 
                    padding: '1em', 
                    borderRadius: '8px', 
                    overflow: 'auto',
                    fontSize: '0.85em',
                    lineHeight: '1.5',
                    color: '#e5e5e7',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    {productTableMarkdown || 'No table extracted'}
                  </pre>
                  <p style={{ fontSize: '0.8em', opacity: 0.6, marginTop: '0.5em' }}>
                    Length: {productTableMarkdown ? productTableMarkdown.length : 0} characters | 
                    productTable prop: {productTable ? `Present (${productTable.length} chars)` : 'null/undefined'}
                  </p>
                </div>
                
                <div style={{ marginTop: '2em', paddingTop: '2em', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ fontSize: '0.9em', opacity: 0.7, marginBottom: '1em' }}>DEBUG: Full Summary</h3>
                  <pre style={{ 
                    background: 'rgba(255, 255, 255, 0.02)', 
                    padding: '1em', 
                    borderRadius: '8px', 
                    overflow: 'auto',
                    fontSize: '0.85em',
                    lineHeight: '1.5',
                    color: '#e5e5e7',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    {summary || 'No summary provided'}
                  </pre>
                  <p style={{ fontSize: '0.8em', opacity: 0.6, marginTop: '0.5em' }}>
                    Length: {summary ? summary.length : 0} characters
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-close-button-footer" onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>
  );

  // Render modal using React Portal to document body for proper overlay
  // This ensures the modal is rendered at the document root level, not within the module's DOM tree
  if (!modalContent) return null;
  return ReactDOM.createPortal(modalContent, document.body);
}

export default SummaryModal;

